import { isString } from 'lodash';

import { ApolloError } from '@apollo/client';
import { reporter } from './hooks/useErrorReporter';

export const ErrorMessages = {
  // Email already synchronised
  emailAlreadySynchronised: 'This email is already syncronised',
};

export enum ErrorTypes {
  // Account blocked errors
  accountLockedError = 'DHAccountLockedError',
  accountPausedError = 'DHAccountPausedError',
  accountCancelledError = 'DHAccountCancelledError',

  // Not found error
  notFoundError = 'DHNotFoundError',

  // Unauthorised
  unauthorisedError = 'DHUnauthorisedError',

  // Contact specific error
  contactExists = 'DHContactExists',
  invalidContactId = 'DHInvalidContactIdError',
  invalidContactFilters = 'DHContactFiltersError',

  // Email errors
  emailNotSynced = 'DHEmailNotSyncedYetError',

  // Cognito errors
  notAuthorized = 'NotAuthorizedException',
  limitExceeded = 'LimitExceededException',
  invalidPassword = 'InvalidParameterException',
  expiredCodeException = 'ExpiredCodeException',
  authUserPoolException = 'AuthUserPoolException',

  customProviderSyncInvalidCredentials = 'DHError',
  invalidFlowBlueprintId = 'DHInvalidFlowBlueprintIdError',

  // Deletion errors
  deletedUserError = 'DHUserDeletedError',
  receivingEntityBeingDeletedError = 'DHReceivingEntityBeingDeleted',
  entitiesAreLockedError = 'DHEntitiesAreLocked',
  officeIsLastForUserError = 'DHOfficeIsLastForUser',
  isLastAccountAdminError = 'DHUserIsLastActiveAccountAdminError',

  // Impersonation errors
  impersonationIsOngoingError = 'DHImpersonationIsOngoingError',

  refreshRequiredError = 'DHRefreshRequiredError',

  // MFA required by account
  mfaRequiredError = 'DHMFARequiredError',
}

const EXPIRED_SESSION_MESSAGES = [
  'session is expired',
  'session has expired',
  'invalid session for the user',
  'refresh token has expired',
  'refresh token is expired',
  'the refresh token is invalid',
  'token has expired',
  'jwt expired',
  'expired token',
] as const;

const hasExpiredSessionMessage = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return EXPIRED_SESSION_MESSAGES.some(m =>
    normalized.includes(m.toLowerCase()),
  );
};

/**
 * Cognito/Amplify can throw NotAuthorizedException for many reasons (wrong password,
 * temporary password expired, etc.). We only treat explicit session-invalid cases
 * as "log out" so we don't sign the user out on every NotAuthorized (e.g. during sign-in).
 */
export const isExpiredOrNotAuthorizedSessionError = (
  error: unknown,
): boolean => {
  if (!error) return false;
  if (typeof error === 'string') return hasExpiredSessionMessage(error);
  if (typeof error !== 'object') return false;

  const err = error as Record<string, unknown>;

  const message =
    (typeof err.message === 'string' && err.message) ||
    (typeof err.error === 'string' && err.error) ||
    '';

  if (message && hasExpiredSessionMessage(message)) return true;

  return false;
};

// Cognito is not very consistent where it places errors. So grab it from the place that exists
export const errortypeFromCognitoError = (err: $Object): string | null => {
  if (isString(err.code)) {
    return err.code;
  } else if (isString(err.type)) {
    return err.type;
  } else if (isString(err.__type)) {
    return err.__type;
  } else {
    reporter.captureException(
      new Error('Could not get the cognito error type'),
      'fatal',
      {
        errorInfo: err,
      },
    );
    return null;
  }
};

export const errorTypeFromMutationError = (
  mutationError?: ApolloError | null,
): string | null => {
  if (!mutationError || !mutationError.graphQLErrors) {
    return null;
  }

  if (mutationError.graphQLErrors.length === 0) {
    return null;
  }

  // Backend ensures we always have an errorType
  // @ts-ignore
  return mutationError.graphQLErrors[0].errorType;
};
